import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { ApplicationState, AppState } from '../../../store';
import { withSnackbar, ProviderContext } from 'notistack';
import { ITranslatorService } from '../../../services/Interfaces/ITranslatorService';
import { ApplicationContext, AppContext } from '../../../context/Contexts';
import { IOrganizationService } from '../../../services/Interfaces/IOrganizationService';
import {
  Box,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  Grid,
  Typography,
  DialogContent,
  InputAdornment,
  Tooltip
} from '@material-ui/core';
import { ScaleLoader } from 'react-spinners';
import AddCircleTwoToneIcon from '@material-ui/icons/AddCircleTwoTone';
import { RouteComponentProps } from 'react-router';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import { OrganizationDomain } from '../../../interfaces/Organization';
import {
  WIDGET_SDK_URL,
  WIDGET_DEFAULT_LOCALE,
  WIDGET_PREFILL_SAMPLE,
  WIDGET_THEME_SAMPLE,
  WIDGET_REGION_VALUES,
  WIDGET_BODY_TYPE_VALUES
} from '../../../helpers/Constants';
import MUIDataTable, { SelectableRows, MUIDataTableMeta } from 'mui-datatables';
import { MUITranslations } from '../../../helpers/MUITableTranslations';
import CloseIcon from '@material-ui/icons/Close';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import DeleteOutlineTwoToneIcon from '@material-ui/icons/DeleteOutlineTwoTone';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import SyncIcon from '@material-ui/icons/Sync';
import CodeIcon from '@material-ui/icons/Code';
import ConfirmDialog from '../../_shared/ConfirmDialog';
import { CopyButton } from '../../../custom-forms';

type DomainConfirmAction = 'delete' | 'regenerate';

const SECRET_KEY_MASK = '••••••••••••';

interface IOrganizationDomainState {
  newDomain: OrganizationDomain | null;
  domains: OrganizationDomain[];
  isLoading: boolean;
  isDialogOpen: boolean;
  pendingConfirm: { action: DomainConfirmAction; id: number } | null;
  isSecretKeyVisible: boolean;
  visibleSecretIds: number[];
}

type IOrganizationDomainProps = { appState: AppState } & ProviderContext &
  RouteComponentProps<{ id: string }>;

class OrganizationDomains extends React.PureComponent<
  IOrganizationDomainProps,
  IOrganizationDomainState
> {
  private translatorService!: ITranslatorService;
  private organizationService!: IOrganizationService;

  static contextType = ApplicationContext;

  private get organizationId(): number {
    return Number.parseInt(this.props.match.params.id);
  }

  state = {
    newDomain: null,
    domains: [],
    isLoading: false,
    isDialogOpen: false,
    pendingConfirm: null,
    isSecretKeyVisible: false,
    visibleSecretIds: []
  } as IOrganizationDomainState;

  public componentDidMount() {
    this.setState({ isLoading: true }, async () => {
      await this.loadData();
    });
  }

  loadData = async () => {
    try {
      const domains = await this.organizationService.GetOrganizationDomains(this.organizationId);
      this.setState({ domains, isLoading: false });
    } catch (error) {
      this.setState({ isLoading: false });
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    }
  };

  private emptyDomain = (): OrganizationDomain => ({
    id: 0,
    organizationId: this.organizationId,
    domain: '',
    token: '',
    turnstileSiteKey: '',
    turnstileSecretKey: '',
    dateCreation: '',
    dateModification: '',
    createdBy: '',
    modifiedBy: ''
  });

  newOrganizationDomain = (domainId: number | null) => {
    const newDomain =
      domainId === null
        ? this.emptyDomain()
        : { ...this.state.domains.find((item) => item.id === domainId)! };

    this.setState({ newDomain, isDialogOpen: true, isSecretKeyVisible: false });
  };

  closeDialog = () => {
    this.setState({ isDialogOpen: false });
  };

  handleFieldChange = (field: keyof OrganizationDomain, value: string) => {
    this.setState({ newDomain: { ...this.state.newDomain!, [field]: value } });
  };

  saveDomain = async () => {
    const newDomain = this.state.newDomain!;
    const { domains } = this.state;

    if (domains.some((item) => item.domain === newDomain.domain && item.id !== newDomain.id)) {
      this.props.enqueueSnackbar(
        this.translatorService.Tranlate('DUPLICATE_DOMAIN', 'Domeniul exista deja!'),
        { variant: 'error' }
      );
      return;
    }

    try {
      this.setState({ isLoading: true, isDialogOpen: false });

      const response = await this.organizationService.SaveOrganizationDomain(newDomain);
      if (!response.success) {
        this.setState({ isLoading: false });
        this.props.enqueueSnackbar(
          this.translatorService.Tranlate(response.message, response.message),
          { variant: 'error' }
        );
        return;
      }

      await this.loadData();

      this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
        variant: 'success'
      });
    } catch (error) {
      this.setState({ isLoading: false });
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    }
  };

  deleteDomain = async (id: number) => {
    try {
      this.setState({ isLoading: true });
      await this.organizationService.DeleteOrganizationDomain(id);
      await this.loadData();

      this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
        variant: 'success'
      });
    } catch (error) {
      this.setState({ isLoading: false });
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    }
  };

  regenerateToken = async (id: number) => {
    try {
      this.setState({ isLoading: true });
      await this.organizationService.RegenerateOrganizationDomainApiKey({ id });
      await this.loadData();

      this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
        variant: 'success'
      });
    } catch (error) {
      this.setState({ isLoading: false });
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    }
  };

  private buildEmbedSnippet = (domain: OrganizationDomain): string =>
    [
      '<div id="carfix-fnol-widget"></div>',
      `<script src="${WIDGET_SDK_URL}"></script>`,
      '<script>',
      '  CarfixFNOL.init({',
      '    container: "#carfix-fnol-widget",',
      `    organizationId: ${this.organizationId},`,
      `    publicToken: "${domain.token}",`,
      `    locale: "${WIDGET_DEFAULT_LOCALE}",`,
      '    prefill: {',
      `      // eventDetails.region - valori acceptate: ${WIDGET_REGION_VALUES.join(', ')}`,
      `      // vehicle.bodyType - valori acceptate: ${WIDGET_BODY_TYPE_VALUES.join(', ')}`,
      `      eventDetails: { caseNumber: "${WIDGET_PREFILL_SAMPLE.eventDetails.caseNumber}", policyNumber: "${WIDGET_PREFILL_SAMPLE.eventDetails.policyNumber}", eventDate: "${WIDGET_PREFILL_SAMPLE.eventDetails.eventDate}", region: "${WIDGET_PREFILL_SAMPLE.eventDetails.region}", eventPlace: "${WIDGET_PREFILL_SAMPLE.eventDetails.eventPlace}" },`,
      `      vehicle: { vin: "${WIDGET_PREFILL_SAMPLE.vehicle.vin}", plateNumber: "${WIDGET_PREFILL_SAMPLE.vehicle.plateNumber}", make: "${WIDGET_PREFILL_SAMPLE.vehicle.make}", model: "${WIDGET_PREFILL_SAMPLE.vehicle.model}", bodyType: "${WIDGET_PREFILL_SAMPLE.vehicle.bodyType}", vehicleTypeCode: "${WIDGET_PREFILL_SAMPLE.vehicle.vehicleTypeCode}", numberOfKilometers: ${WIDGET_PREFILL_SAMPLE.vehicle.numberOfKilometers} },`,
      `      client: { personTypeId: ${WIDGET_PREFILL_SAMPLE.client.personTypeId}, firstName: "${WIDGET_PREFILL_SAMPLE.client.firstName}", lastName: "${WIDGET_PREFILL_SAMPLE.client.lastName}", phone: "${WIDGET_PREFILL_SAMPLE.client.phone}", email: "${WIDGET_PREFILL_SAMPLE.client.email}", address: "${WIDGET_PREFILL_SAMPLE.client.address}" }`,
      '    },',
      `    theme: { primaryColor: "${WIDGET_THEME_SAMPLE.primaryColor}", logoUrl: "${WIDGET_THEME_SAMPLE.logoUrl}" },`,
      '    onComplete: function (result) { /* ... */ }',
      '  });',
      '</script>'
    ].join('\n');

  copyEmbedSnippet = async (id: number) => {
    const domain = this.state.domains.find((item) => item.id === id);
    if (!domain || !domain.token) {
      return;
    }
    try {
      await navigator.clipboard.writeText(this.buildEmbedSnippet(domain));
      this.props.enqueueSnackbar(this.translatorService.Tranlate('COPIED', 'Copiat'), {
        variant: 'success'
      });
    } catch (error) {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    }
  };

  private renderCopyCell = (value: string) => {
    if (!value) {
      return '-';
    }

    return (
      <Box display="flex" alignItems="center">
        <span className="text-truncate" style={{ maxWidth: 320 }}>
          {value}
        </span>
        <CopyButton
          value={value}
          tooltip={this.translatorService.Tranlate('COPY', 'Copiaza')}
          onCopied={() =>
            this.props.enqueueSnackbar(this.translatorService.Tranlate('COPIED', 'Copiat'), {
              variant: 'success'
            })
          }
        />
      </Box>
    );
  };

  private isSecretVisible = (tableMeta: MUIDataTableMeta) => {
    return this.state.visibleSecretIds.includes(tableMeta.rowData[0] as number);
  };

  private toggleSecretVisibility = (id: number) => {
    const { visibleSecretIds } = this.state;

    this.setState({
      visibleSecretIds: visibleSecretIds.includes(id)
        ? visibleSecretIds.filter((item) => item !== id)
        : [...visibleSecretIds, id]
    });
  };

  private renderSecretCell = (value: string, tableMeta: MUIDataTableMeta) => {
    if (!value) {
      return '-';
    }

    const isVisible = this.isSecretVisible(tableMeta);

    return (
      <Box display="flex" alignItems="center">
        <span className="text-truncate" style={{ maxWidth: 320 }}>
          {isVisible ? value : SECRET_KEY_MASK}
        </span>
        {isVisible ? (
          <CopyButton
            value={value}
            tooltip={this.translatorService.Tranlate('COPY', 'Copiaza')}
            onCopied={() =>
              this.props.enqueueSnackbar(this.translatorService.Tranlate('COPIED', 'Copiat'), {
                variant: 'success'
              })
            }
          />
        ) : null}
        <Tooltip
          title={
            isVisible
              ? this.translatorService.Tranlate('HIDE', 'Ascunde')
              : this.translatorService.Tranlate('SHOW', 'Arata')
          }
        >
          <IconButton
            size="small"
            onClick={() => this.toggleSecretVisibility(tableMeta.rowData[0] as number)}
          >
            {isVisible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  getColumns = () => {
    return [
      {
        name: 'id',
        options: { display: 'excluded' as const, filter: false }
      },
      {
        name: 'domain',
        label: this.translatorService.Tranlate('ORGANIZATION_DOMAINS_DOMAIN_HEADER', 'Domeniu')
      },
      {
        name: 'token',
        label: this.translatorService.Tranlate('ORGANIZATION_DOMAINS_TOKEN_HEADER', 'Token'),
        options: {
          filter: false,
          customBodyRender: this.renderCopyCell
        }
      },
      {
        name: 'turnstileSiteKey',
        label: this.translatorService.Tranlate(
          'ORGANIZATION_DOMAINS_TURNSTILE_SITE_KEY_HEADER',
          'Turnstile site key'
        ),
        options: {
          filter: false,
          customBodyRender: this.renderCopyCell
        }
      },
      {
        name: 'turnstileSecretKey',
        label: this.translatorService.Tranlate(
          'ORGANIZATION_DOMAINS_TURNSTILE_SECRET_KEY_HEADER',
          'Turnstile secret key'
        ),
        options: {
          filter: false,
          customBodyRender: this.renderSecretCell
        }
      },
      {
        name: '',
        options: {
          viewColumns: false,
          filter: false,
          setCellHeaderProps: () => ({ align: 'center' }),
          setCellProps: () => ({ align: 'center' }),
          customBodyRender: (value: any, tableMeta: MUIDataTableMeta) => {
            const id = tableMeta.rowData[0];
            return (
              <Fragment>
                <Tooltip
                  title={this.translatorService.Tranlate(
                    'ORGANIZATION_DOMAINS_COPY_SNIPPET',
                    'Copiaza codul de integrare'
                  )}
                >
                  <IconButton
                    aria-label="snippet"
                    color="inherit"
                    className="text-primary"
                    size="small"
                    onClick={() => this.copyEmbedSnippet(id)}
                  >
                    <CodeIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={this.translatorService.Tranlate(
                    'ORGANIZATION_DOMAINS_REGENERATE_TOKEN',
                    'Regenereaza token'
                  )}
                >
                  <IconButton
                    aria-label="regenerate"
                    color="inherit"
                    className="text-primary"
                    size="small"
                    onClick={() => this.openConfirm('regenerate', id)}
                  >
                    <SyncIcon />
                  </IconButton>
                </Tooltip>
                <IconButton
                  aria-label="edit"
                  color="inherit"
                  className="text-primary"
                  size="small"
                  onClick={() => this.newOrganizationDomain(id)}
                >
                  <EditOutlinedIcon />
                </IconButton>
                <IconButton
                  aria-label="delete"
                  color="inherit"
                  className="text-danger"
                  size="small"
                  onClick={() => this.openConfirm('delete', id)}
                >
                  <DeleteOutlineTwoToneIcon />
                </IconButton>
              </Fragment>
            );
          }
        }
      }
    ];
  };

  renderAddForm = () => {
    const { newDomain } = this.state;

    if (!newDomain) return null;

    return (
      <ValidatorForm onSubmit={() => this.saveDomain()}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary" className="m-2">
              {this.translatorService.Tranlate(
                'ORGANIZATION_DOMAINS_DOMAIN_HINT',
                'Introdu domeniul (host-ul), fara schema; se compara cu host-ul din header-ul Origin trimis de browser'
              )}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextValidator
              fullWidth
              name="domain"
              className="m-2"
              id="domain"
              value={newDomain.domain ?? ''}
              onChange={(e: any) => this.handleFieldChange('domain', e.target.value)}
              label={this.translatorService.Tranlate(
                'ORGANIZATION_DOMAINS_DOMAIN_LABEL',
                'Domeniu (ex. test-fleet.carfixconnect.ro)'
              )}
              placeholder="test-fleet.carfixconnect.ro"
              validators={['required']}
              errorMessages={[
                this.translatorService.Tranlate('VALIDATORS_REQUIRED', 'Campul este obligatoriu')
              ]}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary" className="m-2">
              {this.translatorService.Tranlate(
                'ORGANIZATION_DOMAINS_TURNSTILE_HINT',
                'Cheile Cloudflare Turnstile ale widget-ului acestui domeniu: site key ajunge in widget, secret key ramane in Connect si verifica token-ul la crearea dosarului'
              )}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextValidator
              fullWidth
              name="turnstileSiteKey"
              className="m-2"
              id="turnstileSiteKey"
              value={newDomain.turnstileSiteKey ?? ''}
              onChange={(e: any) => this.handleFieldChange('turnstileSiteKey', e.target.value)}
              label={this.translatorService.Tranlate(
                'ORGANIZATION_DOMAINS_TURNSTILE_SITE_KEY_LABEL',
                'Turnstile site key'
              )}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CopyButton
                      value={newDomain.turnstileSiteKey ?? ''}
                      tooltip={this.translatorService.Tranlate('COPY', 'Copiaza')}
                      onCopied={() =>
                        this.props.enqueueSnackbar(
                          this.translatorService.Tranlate('COPIED', 'Copiat'),
                          { variant: 'success' }
                        )
                      }
                    />
                  </InputAdornment>
                )
              }}
              validators={['required']}
              errorMessages={[
                this.translatorService.Tranlate('VALIDATORS_REQUIRED', 'Campul este obligatoriu')
              ]}
            />
          </Grid>
          <Grid item xs={12}>
            <TextValidator
              fullWidth
              name="turnstileSecretKey"
              className="m-2"
              id="turnstileSecretKey"
              type={this.state.isSecretKeyVisible ? 'text' : 'password'}
              value={newDomain.turnstileSecretKey ?? ''}
              onChange={(e: any) => this.handleFieldChange('turnstileSecretKey', e.target.value)}
              label={this.translatorService.Tranlate(
                'ORGANIZATION_DOMAINS_TURNSTILE_SECRET_KEY_LABEL',
                'Turnstile secret key'
              )}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip
                      title={
                        this.state.isSecretKeyVisible
                          ? this.translatorService.Tranlate('HIDE', 'Ascunde')
                          : this.translatorService.Tranlate('SHOW', 'Arata')
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={() =>
                          this.setState({ isSecretKeyVisible: !this.state.isSecretKeyVisible })
                        }
                      >
                        {this.state.isSecretKeyVisible ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    <CopyButton
                      value={newDomain.turnstileSecretKey ?? ''}
                      tooltip={this.translatorService.Tranlate('COPY', 'Copiaza')}
                      onCopied={() =>
                        this.props.enqueueSnackbar(
                          this.translatorService.Tranlate('COPIED', 'Copiat'),
                          { variant: 'success' }
                        )
                      }
                    />
                  </InputAdornment>
                )
              }}
              validators={['required']}
              errorMessages={[
                this.translatorService.Tranlate('VALIDATORS_REQUIRED', 'Campul este obligatoriu')
              ]}
            />
          </Grid>
          <Grid item xs={12} className="text-right">
            <Button className="m-2" variant="contained" color="primary" type="submit">
              {this.translatorService.Tranlate('SAVE', 'Salveaza')}
            </Button>
          </Grid>
        </Grid>
      </ValidatorForm>
    );
  };

  openConfirm = (action: DomainConfirmAction, id: number) => {
    this.setState({ pendingConfirm: { action, id } });
  };

  closeConfirm = () => {
    this.setState({ pendingConfirm: null });
  };

  confirmPendingAction = async () => {
    const { pendingConfirm } = this.state;
    if (!pendingConfirm) {
      return;
    }
    this.setState({ pendingConfirm: null });
    if (pendingConfirm.action === 'delete') {
      await this.deleteDomain(pendingConfirm.id);
    } else {
      await this.regenerateToken(pendingConfirm.id);
    }
  };

  renderConfirmDialog = () => {
    const { pendingConfirm } = this.state;
    const isDelete = pendingConfirm?.action === 'delete';

    return (
      <ConfirmDialog
        open={pendingConfirm !== null}
        onConfirm={() => this.confirmPendingAction()}
        onClose={() => this.closeConfirm()}
        title={
          isDelete
            ? this.translatorService.Tranlate(
                'ORGANIZATION_DOMAINS_DELETE_CONFIRM_TITLE',
                'Stergi domeniul?'
              )
            : this.translatorService.Tranlate(
                'ORGANIZATION_DOMAINS_REGENERATE_CONFIRM_TITLE',
                'Regenerezi token-ul?'
              )
        }
        message={
          isDelete
            ? this.translatorService.Tranlate(
                'ORGANIZATION_DOMAINS_DELETE_CONFIRM_MESSAGE',
                'Widget-ul nu va mai putea fi folosit de pe acest domeniu.'
              )
            : this.translatorService.Tranlate(
                'ORGANIZATION_DOMAINS_REGENERATE_CONFIRM_MESSAGE',
                'Token-ul curent va fi invalidat; integrarea asiguratorului trebuie actualizata cu noul token.'
              )
        }
        yesLabel={this.translatorService.Tranlate('WORKFLOW_DOCUMENTS_TYPE_TAB_YES', 'Da')}
        noLabel={this.translatorService.Tranlate('WORKFLOW_DOCUMENTS_TYPE_TAB_NO', 'Nu')}
      />
    );
  };

  public render() {
    this.translatorService = (this.context as AppContext).translatorService;
    this.organizationService = (this.context as AppContext).organizationService;

    const options = {
      filter: true,
      selectableRows: 'none' as SelectableRows,
      selectableRowsOnClick: false,
      print: false,
      download: false,
      textLabels: MUITranslations.GetTranslations(this.translatorService),
      sort: false,
      customToolbar: () => {
        return (
          <Tooltip title={this.translatorService.Tranlate('Add', 'Adauga')}>
            <IconButton
              aria-label="add"
              color="secondary"
              className="m-2"
              onClick={() => this.newOrganizationDomain(null)}
            >
              <AddCircleTwoToneIcon />
            </IconButton>
          </Tooltip>
        );
      }
    };

    return (
      <Fragment>
        <Box mt={1} pt={1}>
          <div className="d-flex flex-row text-center flex-wrap justify-content-center">
            <ScaleLoader color={'var(--primary)'} loading={this.state.isLoading} />
          </div>
          {!this.state.isLoading ? (
            <MUIDataTable
              title={''}
              data={this.state.domains}
              columns={this.getColumns()}
              options={options}
            />
          ) : null}
        </Box>
        <Dialog
          onClose={this.closeDialog}
          aria-labelledby="customized-dialog-title"
          open={this.state.isDialogOpen}
          fullWidth={true}
          maxWidth={'sm'}
        >
          <DialogTitle id="customized-dialog-title">
            <Grid container>
              <Grid item xs={11}>
                <Typography variant="h4">
                  {this.state.newDomain && this.state.newDomain.id === 0
                    ? this.translatorService.Tranlate(
                        'ORGANIZATION_DOMAIN_ADD_TITLE',
                        'Adauga domeniu'
                      )
                    : this.translatorService.Tranlate(
                        'ORGANIZATION_DOMAIN_UPDATE_TITLE',
                        'Actualizeaza domeniu'
                      )}
                </Typography>
              </Grid>
              <Grid item xs={1}>
                <IconButton aria-label="close" onClick={this.closeDialog} size={'small'}>
                  <CloseIcon />
                </IconButton>
              </Grid>
            </Grid>
          </DialogTitle>
          <DialogContent dividers>{this.renderAddForm()}</DialogContent>
        </Dialog>
        {this.renderConfirmDialog()}
      </Fragment>
    );
  }
}

export default connect(
  (state: ApplicationState) => ({
    appState: state.app
  }),
  null
)(withSnackbar(OrganizationDomains as any));
